import { computed, ref } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { apiScheduleTelemed, apiScheduleDoctors } from 'www/api'
import { DAYS_FOR_DOCTOR_SLOTS } from 'www/modules/Schedule/constants'
import { PlaceType } from 'www/vue_components/common/DoctorPriceBlock/types'
import { getPriceList, sortLpuAddressList } from 'www/vue_components/common/DoctorPageLpuList/functions'
import { useExternalDataStore } from 'www/vue_components/common/DoctorPageLpuList/stores'
import type { LpuAddressList } from 'www/vue_components/common/DoctorPageLpuList/types'
import { useDoctorAppointmentCardStore } from 'www/vue_components/common/DoctorPageLpuList/components/DoctorAppointmentCard/stores'
import { getTimezoneOffsetInHours } from 'www/utils'
import { PLACE_TYPES } from 'www/vue_components/common/DoctorPriceBlock/constants'

const locationHash = window.location.hash
let placeTypeFromHash: PlaceType = 'clinic'

if (locationHash.includes('#filter=')) {
  const hashFilterValue = locationHash.match(/#filter=(\w+)/)?.[1] || ''

  if ((PLACE_TYPES as ReadonlyArray<string>).includes(hashFilterValue)) {
    placeTypeFromHash = hashFilterValue as PlaceType
  }
}

export default defineStore('doctorPageLpuListStore', () => {
  const externalDataStore = useExternalDataStore()
  const { lpuAddressList, currentDate, townTimedelta } = storeToRefs(externalDataStore)

  const filterType = ref<PlaceType>(placeTypeFromHash)
  const expandedPanels = ref<number[]>([0])

  function checkAppointmentPriceByFilter(workplace: LpuAddressList[number]['workplaces'][number]) {
    const priceList = getPriceList(workplace)

    switch (filterType.value) {
      case 'clinic':
        return typeof priceList.price === 'number'
      case 'home':
        return typeof priceList.priceHome === 'number'
      case 'telemed':
        return typeof priceList.priceOnline === 'number' || typeof priceList.priceMedtochkaTelemed === 'number'
    }
  }

  const lpuAddressListFiltered = computed(
    () => sortLpuAddressList(lpuAddressList.value?.filter(
      lpuAddress => filterType.value === 'clinic'
      || lpuAddress.workplaces.some(workplace => checkAppointmentPriceByFilter(workplace)),
    ), filterType.value) || [],
  )

  async function updateSlots() {
    if (!lpuAddressList.value || filterType.value === 'home') {
      return
    }

    lpuAddressList.value.forEach(lpuAddress => {
      useDoctorAppointmentCardStore(lpuAddress.lpu.id)().isRequestSlotsDone = false
      useDoctorAppointmentCardStore(lpuAddress.lpu_id)().resetSlots()
    })

    try {
      if (filterType.value === 'telemed') {
        const filterdLpuAddressList = lpuAddressList.value.filter(
          lpuAddress => (
            lpuAddress.appointment_telemed
            && lpuAddress.appointment_direct
            && lpuAddress.has_slots_telemed_in_clinic
          ) || (
            lpuAddress.appointment_telemed
            && lpuAddress.enable_appointment_telemed_in_medtochka
            && lpuAddress.has_slots_telemed_in_medtochka
          ),
        )

        if (!filterdLpuAddressList.length) {
          throw new Error('Массив "lpu_params" не должен быть пустым')
        }

        const response = await apiScheduleTelemed.post({
          days: DAYS_FOR_DOCTOR_SLOTS,
          user_start_date: currentDate.value,
          only_free: true,
          user_timezone: getTimezoneOffsetInHours(),
          lpu_params: filterdLpuAddressList
            .map(lpuAddress => ({
              lpu_id: lpuAddress.lpu_id,
              schedule_params: [{
                doctor_id: lpuAddress.doctor_id ?? 0,
                type_schedule: useDoctorAppointmentCardStore(lpuAddress.lpu_id)().scheduleType,
              }],
            })),
        })

        response.data.forEach(responseItem => {
          useDoctorAppointmentCardStore(responseItem.lpu_id)().calendarSlots = apiScheduleTelemed.transformSlots(
            responseItem.schedule_data[0]?.schedule,
          )
        })
      } else if (filterType.value === 'clinic') {
        const filterdLpuAddressList = lpuAddressList.value.filter(
          lpuAddress => lpuAddress.has_slots_in_clinic,
        )

        if (!filterdLpuAddressList.length) {
          throw new Error('Массив "doctors_lpus" не должен быть пустым')
        }

        const response = await apiScheduleDoctors.post({
          days: DAYS_FOR_DOCTOR_SLOTS,
          dt_start: currentDate.value,
          all_slots: false,
          town_timedelta: townTimedelta.value ?? 0,
          doctors_lpus: filterdLpuAddressList.map(lpuAddress => ({
            doctor_id: lpuAddress.doctor_id ?? 0,
            lpu_id: lpuAddress.lpu_id,
            lpu_timedelta: lpuAddress.lpu.town.timedelta ?? 0,
            has_slots: lpuAddress.has_slots,
          })),
        })

        response.data.result.forEach(responseItem => {
          useDoctorAppointmentCardStore(responseItem.lpu_id)().calendarSlots = responseItem.slots
        })
      }
    } catch {
      lpuAddressList.value?.forEach(lpuAddress => {
        useDoctorAppointmentCardStore(lpuAddress.lpu.id)().calendarSlots = {}
      })
    } finally {
      lpuAddressList.value?.forEach(lpuAddress => {
        useDoctorAppointmentCardStore(lpuAddress.lpu.id)().isRequestSlotsDone = true
      })
    }
  }

  return {
    filterType,
    expandedPanels,
    lpuAddressListFiltered,
    updateSlots,
  }
})
